import type { ContactDetailsInfoContentProps } from './index.types';
import { ReactComponent as CallHistoryIcon } from 'Assets/icons/dotpoints.svg';
import { useViewCallHistory } from 'Components/CallLogs/CallsDirectoryList/hooks/useViewCallHistory';
import { EmailTypes, PhoneNumberTypes } from 'Constants/enums';
import { COMMUNICATOR_HIDE_CHAT_CALL_HISTORY } from 'Constants/featureFlags';
import useFeatureFlags from 'Hooks/featureFlags/useFeatureFlags';
import { ContactModelBase } from 'Models/ContactModel';
import { ContactModelV2 } from 'Models/ContactModelV2';
import React from 'react';
import {
  ANALYTICS_EVENT_CONTACT_DETAILS_CALL_HISTORY_SELECTED,
  pushToGTMDataLayer,
} from 'Utils/analytics';
import { Button } from '~/lib/ui/button';
import { ContactSaveAction } from '../../ContactSaveAction';
import { ContactDetailsInfoDuplicates } from '../ContactDetailsInfoDuplicates';

/**
 * ContactDetailsInfoContent is a component that displays the contact details in the sidebar.
 */
export const ContactDetailsInfoContent: React.FC<
  ContactDetailsInfoContentProps
> = ({ contact, openContactDetails }) => {
  const viewCallHistory = useViewCallHistory();

  const { [COMMUNICATOR_HIDE_CHAT_CALL_HISTORY]: hideChatCallHistory } =
    useFeatureFlags([COMMUNICATOR_HIDE_CHAT_CALL_HISTORY]);

  // Handle ContactModelBase (external contact)
  if (contact instanceof ContactModelBase) {
    return <ContactSaveAction contact={contact} />;
  }

  // Handle contact with no details
  if (
    !contact.phoneNumbers?.length &&
    !contact.extensionNumbers?.length &&
    !contact.emails?.length
  ) {
    return <p>No contact details added yet.</p>;
  }

  const handleViewCallHistory = () => {
    pushToGTMDataLayer(ANALYTICS_EVENT_CONTACT_DETAILS_CALL_HISTORY_SELECTED);
    viewCallHistory({ contact });
  };

  // Handle contact with details
  return (
    <>
      {contact.nonDuplicatePhoneNumbers?.map((phoneNumber) => (
        <div key={phoneNumber.number} className="px-2">
          <div>
            {
              PhoneNumberTypes?.find((type) => type.key === phoneNumber.type)
                .text
            }
          </div>
          <div>{ContactModelV2.formatNumber(phoneNumber.number)}</div>
        </div>
      ))}

      {contact.extensionNumbers?.map((extension) => (
        <div key={extension.number} className="px-2">
          <div>Extension</div>
          <div>{extension.number}</div>
        </div>
      ))}

      {contact.nonDuplicateEmails?.map((email) => (
        <div key={email.address} className="px-2">
          <div>{EmailTypes?.find((type) => type.key === email.type).text}</div>
          <Button variant="link" className="anchor" asChild>
            <a
              href={`mailto:${email.address}`}
              onClick={() => pushToGTMDataLayer('contact_detail_email_click')}
            >
              {email.address}
            </a>
          </Button>
        </div>
      ))}
      {hideChatCallHistory && (
        <Button
          variant="ghost"
          className="w-full justify-start gap-[5.25px] p-[5.25px]! font-normal my-2"
          aria-label={`View call history for ${contact.name}`}
          onClick={handleViewCallHistory}
        >
          <CallHistoryIcon aria-hidden className="size-[21px]" />
          View call history
        </Button>
      )}
      <ContactDetailsInfoDuplicates
        phoneNumbers={contact.nonDuplicatePhoneNumbers}
        contact={contact}
        openContactDetails={openContactDetails}
      />
    </>
  );
};
